// /client/ui/ConfirmationModal.jsx
import React, { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import ModalOrSheet from "./ModalOrSheet.jsx";
import AppButton from "./AppButton.jsx";
import { cn } from "../../utils/themeClasses.js";
import { Input, Textarea } from "../forms/shared/FormAtoms.jsx";
import { detailPageHeaderNavSecondaryShellClassName, entityRowDeleteActionClassName } from "../../design/tokens.js";

/**
 * useConfirm(options?) -> Promise<{ ok: boolean, reason?: string }>
 *
 * Optionen (alle optional):
 * - title, message, confirmLabel, cancelLabel
 * - variant: "default" | "danger" | "warning" | "success"
 * - destructive: boolean (Alias für variant="danger")
 * - requireReason: boolean
 * - placeholder, defaultReason
 * - inputType: "input" | "textarea"
 * - escCloses: boolean (default true)
 * - closeOnOverlay: boolean (default true)
 * - maxWidth: "md" | "lg" | number (px)
 * - confirmHotkey: "ctrlEnter" | "enter" | false
 *   (default: "ctrlEnter" bei textarea, sonst "enter")
 * - autoFocus: "input" | "confirm" | "none" (default s. oben)
 * - icon: ReactNode
 * - onBeforeConfirm?: (reason) => Promise<boolean> | boolean
 */
const ConfirmCtx = createContext(null);

/* ---------- Helpers ---------- */
function once(fn) {
  let called = false;
  return (...args) => {
    if (called) return;
    called = true;
    fn?.(...args);
  };
}
const clampPx = v => typeof v === "number" ? `${Math.max(320, Math.min(1200, v))}px` : v;
export function ConfirmProvider({
  children
}) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState({});
  const resolverRef = useRef(null);
  const openerRef = useRef(null); // fokussiertes Element beim Öffnen
  const runningRef = useRef(false); // Single-flight Guard

  const confirm = useCallback((opts = {}) => {
    return new Promise(resolve => {
      // Falls bereits offen → das alte Promise sauber auflösen
      if (runningRef.current) {
        try {
          resolverRef.current?.({
            ok: false,
            reason: "superseded"
          });
        } catch {}
      }
      resolverRef.current = once(resolve);
      setOptions(opts || {});
      openerRef.current = typeof document !== "undefined" ? document.activeElement : null;
      runningRef.current = true;
      setOpen(true);
      // Scroll-Lock per Klasse (robuster als nur inline style)
      if (typeof document !== "undefined") {
        document.body.classList.add("modal-open");
      }
    });
  }, []);
  const closeOnly = useCallback(() => {
    setOpen(false);
    if (typeof document !== "undefined") {
      document.body.classList.remove("modal-open");
      document.body.style.paddingRight = ""; // evtl. auf Null setzen
    }
  }, []);
  const handleClose = useCallback(() => {
    closeOnly();
    resolverRef.current?.({
      ok: false
    });
    resolverRef.current = null;
    runningRef.current = false;
    setTimeout(() => openerRef.current?.focus?.(), 30); // Safari-Delay
  }, [closeOnly]);
  const handleConfirm = useCallback(async reason => {
    try {
      if (typeof options.onBeforeConfirm === "function") {
        const ok = await options.onBeforeConfirm(reason);
        if (!ok) return; // Validierung fehlgeschlagen → Modal bleibt offen
      }
    } catch {
      return; // Fehler in onBeforeConfirm → nicht schließen
    }
    closeOnly();
    resolverRef.current?.({
      ok: true,
      reason
    });
    resolverRef.current = null;
    runningRef.current = false;
    setTimeout(() => openerRef.current?.focus?.(), 30);
  }, [closeOnly, options]);
  const value = useMemo(() => ({
    confirm
  }), [confirm]);
  return <ConfirmCtx.Provider value={value}>
      {children}
      <ConfirmModal open={open} options={options} onClose={handleClose} onConfirm={handleConfirm} />
    </ConfirmCtx.Provider>;
}
export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) throw new Error("useConfirm must be used within <ConfirmProvider>");
  return ctx.confirm;
}

/* ================= Modal UI ================= */

function ConfirmModal({
  open,
  options,
  onClose,
  onConfirm
}) {
    const modalBaseId = useId().replace(/:/g, "");
  const titleId = `confirm-title-${modalBaseId}`;
  const descId = `confirm-desc-${modalBaseId}`;
  const reasonId = `confirm-reason-${modalBaseId}`;
  // SSR-Guard
  if (typeof document === "undefined") return null;
  const {
    title = "Bestätigen",
    message = "Möchtest du wirklich fortfahren?",
    confirmLabel = "Bestätigen",
    cancelLabel = "Abbrechen",
    variant = options.destructive ? "danger" : options.variant || "default",
    requireReason = false,
    placeholder = "Bitte Wert eingeben…",
    defaultReason = "",
    inputType = "input",
    escCloses = true,
    closeOnOverlay = true,
    maxWidth = "md",
    confirmHotkey = inputType === "textarea" ? "ctrlEnter" : "enter",
    autoFocus = requireReason ? "input" : "confirm",
    icon,
    hideCancel = false,
  } = options || {};
  const [reason, setReason] = useState(defaultReason ?? "");
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const confirmRef = useRef(null);

  /* ---------- Scrollbar-Gap + Autofokus ---------- */
  useEffect(() => {
    if (!open) return;
    const prevPadRight = document.body.style.paddingRight;
    const scrollbarGap = window.innerWidth - document.documentElement.clientWidth;

    // Verhindert Layout-Jump beim Overflow-Hidden
    if (scrollbarGap > 0) {
      document.body.style.paddingRight = `${scrollbarGap}px`;
    }
    setReason(defaultReason ?? "");
    const t = setTimeout(() => {
      if (autoFocus === "input") inputRef.current?.focus();else if (autoFocus === "confirm") confirmRef.current?.focus();
    }, 20);
    return () => {
      document.body.style.paddingRight = prevPadRight;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /* ---------- ESC / Focus-Trap / Hotkeys ---------- */
  useEffect(() => {
    if (!open) return;
    const onKey = e => {
      if (e.isComposing) return; // IME-Schutz
      if (e.key === "Escape" && escCloses) {
        e.preventDefault();
        onClose?.();
        return;
      }
      if (confirmHotkey && !e.defaultPrevented) {
        const enter = e.key === "Enter";
        const withCtrl = e.ctrlKey || e.metaKey;
        const canConfirm = !requireReason || !!reason.trim();
        const hitEnter = confirmHotkey === "enter" && enter && !withCtrl || confirmHotkey === "ctrlEnter" && enter && withCtrl;
        if (hitEnter && canConfirm) {
          e.preventDefault();
          onConfirm?.(requireReason ? reason.trim() : undefined);
          return;
        }
      }

      // Einfacher Fokus-Trap
      if (e.key === "Tab") {
        const root = panelRef.current;
        if (!root) return;
        const nodes = root.querySelectorAll("button, a, input, textarea, select, [tabindex]:not([tabindex='-1'])");
        const list = Array.from(nodes).filter(el => !el.hasAttribute("disabled") && el.getAttribute("aria-hidden") !== "true");
        if (list.length === 0) {
          e.preventDefault();
          panelRef.current?.focus?.();
          return;
        }
        if (list.length === 1) {
          e.preventDefault();
          list[0]?.focus?.();
          return;
        }
        const first = list[0];
        const last = list[list.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, escCloses, confirmHotkey, onClose, onConfirm, requireReason, reason]);
  if (!open) return null;

  /* ---------- Farbpalette ---------- */
  const paletteMap = {
    default: {
      btn: "bg-blue-600 hover:bg-blue-500",
      ring: "focus:ring-blue-400/40",
      iconBg: "bg-white/10",
      titleDark: "text-blue-50",
      titleLight: "text-slate-900"
    },
    danger: {
      btn: "bg-red-600 hover:bg-red-500",
      ring: "focus:ring-red-400/40",
      iconBg: "bg-red-500/15",
      titleDark: "text-red-100",
      titleLight: "text-red-700"
    },
    warning: {
      btn: "bg-amber-600 hover:bg-amber-500",
      ring: "focus:ring-amber-400/40",
      iconBg: "bg-amber-500/15",
      titleDark: "text-amber-100",
      titleLight: "text-amber-700"
    },
    success: {
      btn: "bg-green-600 hover:bg-green-500",
      ring: "focus:ring-green-400/40",
      iconBg: "bg-green-500/15",
      titleDark: "text-green-100",
      titleLight: "text-green-700"
    }
  };
  const palette = paletteMap[variant] || paletteMap.default;
  const confirmBtnVariant =
    variant === "danger" ? "secondary" : variant === "success" ? "success" : "primary";
  const canConfirm = !requireReason || !!reason.trim();
  const describedBy = message ? descId : requireReason ? reasonId : undefined;
  const styleMax = typeof maxWidth === "number" ? {
    maxWidth: clampPx(maxWidth)
  } : {};

  /* ---------- Klassen ---------- */
  const panelClasses = ["relative", "w-full", "outline-none", "confirm-panel-in",
  // Animation
  "confirm-modal",
  // gezieltes CSS-Targeting
  "transition-all"].filter(Boolean).join(" ");
  const inputBase = ["w-full", "rounded-lg", "border", "focus:outline-none", "focus:ring-2", palette.ring, "p-2", "text-[16px]", "md:text-sm", "allow-caret",
  // Caret sichtbar trotz globalem Default
  "transition-all", "min-h-[44px]", "bg-surface-elevated border-default text-primary placeholder:text-muted"].join(" ");
  /** Gefüllte Bestätigung (Primär/Warnung/OK) — nicht für danger (siehe unten entityRowDelete…) */
  const confirmBtnFilledClasses = ["w-full", "md:w-auto", "min-h-[44px]", "md:min-h-9", "px-3", "py-1.5", "rounded-lg", "text-white", "shadow-sm", "transition", palette.btn, "disabled:opacity-50", "disabled:cursor-not-allowed", "focus:outline-none", "focus:ring-2", palette.ring].join(" ");
  const titleClasses = ["text-base", "md:text-lg", "font-semibold", palette.titleDark].join(" ");
  return <ModalOrSheet open={open} onClose={onClose} title="" ariaLabel={variant === "danger" ? "Warnung" : "Bestätigen"} maxWidth={maxWidth} closeOnOverlay={closeOnOverlay} escCloses={escCloses}>
      <div ref={panelRef} className={panelClasses} style={styleMax} data-variant={variant} role={variant === "danger" ? "alertdialog" : "dialog"} aria-labelledby={titleId} aria-describedby={describedBy}>
        {/* Header */}
        <div className="mb-3 flex items-start gap-3">
          {icon ? <div className={["shrink-0 rounded-xl p-2", palette.iconBg].join(" ")} aria-hidden="true">
              {icon}
            </div> : null}
          <div className="flex-1 min-w-0">
            <h3 id={titleId} className={titleClasses}>
              {title}
            </h3>
            {!!message && (typeof message === "string" ? <p id={descId} className={`mt-1 text-sm ${"text-blue-200/85"}`}>
                  {message}
                </p> : <div id={descId} className="mt-1">{message}</div>)}
          </div>
        </div>

        {/* Reason */}
        {requireReason && <div className="mb-2">
            <label htmlFor={reasonId} className="sr-only">Begründung</label>
            {inputType === "textarea" ? <Textarea id={reasonId} ref={inputRef} rows={4} value={reason} onChange={e => setReason(e.target.value)} placeholder={placeholder} className={[inputBase, "resize-none"].join(" ")} /> : <Input id={reasonId} ref={inputRef} type="text" value={reason} onChange={e => setReason(e.target.value)} placeholder={placeholder} className={inputBase} />}
          </div>}

        {/* Actions */}
        <div className="mt-4 flex flex-col gap-2 md:flex-row md:justify-end">
          {!hideCancel ? (
          <AppButton
            type="button"
            variant="secondary"
            onClick={() => onClose?.()}
            className={cn(
              "w-full md:w-auto min-h-[44px] md:min-h-9 px-3 py-1.5 font-normal shrink-0",
              detailPageHeaderNavSecondaryShellClassName,
            )}
          >
            {cancelLabel}
          </AppButton>
          ) : null}
          <AppButton
            ref={confirmRef}
            type="button"
            variant={confirmBtnVariant}
            data-role="primary-btn"
            disabled={!canConfirm}
            onClick={() => onConfirm?.(requireReason ? reason.trim() : undefined)}
            className={cn(
              "w-full md:w-auto min-h-[44px] md:min-h-9 px-3 py-1.5 shrink-0",
              variant === "danger" && entityRowDeleteActionClassName,
              variant === "success" && "font-medium",
              variant !== "danger" && variant !== "success" && cn("font-normal", confirmBtnFilledClasses),
            )}
          >
            {confirmLabel}
          </AppButton>
        </div>

        {/* Subtiler Inset-Glow + Safe-Area */}
        <div className="pointer-events-none rounded-b-2xl" style={{
        boxShadow: "inset 0 -24px 48px rgba(59,130,246,0.08)"
      }} aria-hidden />
        <div className="h-[env(safe-area-inset-bottom)]" aria-hidden="true" />
      </div>
    </ModalOrSheet>;
}